import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useSearchStudentsQuery } from '@teachur/http-client';
import type { StudentSearchRow } from '@teachur/http-client';
import { cn } from '@new/lib';
import { SearchInput } from '@new/common/ui/SearchInput/SearchInput';

export interface SelectedStudent {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export interface StudentSearchSelectProps {
  value: SelectedStudent | null;
  onChange: (student: SelectedStudent | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const studentName = (student: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) => {
  const name = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  return name || student.email || 'Unnamed student';
};

const toSelected = (row: StudentSearchRow): SelectedStudent => ({
  id: row.id,
  firstName: row.firstName ?? null,
  lastName: row.lastName ?? null,
  email: row.email ?? null,
});

/**
 * Student picker built as a plain search field with an open results dropdown
 * underneath (NOT a select/combobox). The typed query is debounced (via
 * `SearchInput`) and pushed to the server-side `GET students-new` through
 * `useSearchStudentsQuery`, so it scales past the 6000+ student list without
 * fetching everything. The selected student is shown as a removable chip above
 * the input, and the input clears after a pick so search stays free.
 */
export const StudentSearchSelect = ({
  value,
  onChange,
  placeholder = 'Search students by name or email',
  disabled,
  className,
}: StudentSearchSelectProps) => {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const { data, isFetching } = useSearchStudentsQuery(
    { searchTerm: term.trim() || undefined },
    { enabled: !disabled }
  );

  const students = useMemo(() => (data?.data ?? []) as StudentSearchRow[], [data]);
  const showDropdown = open && !disabled && term.trim().length > 0;

  const handleSelect = (row: StudentSearchRow) => {
    onChange(toSelected(row));
    setTerm('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-2', className)}>
      {/* Selected student chip */}
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-[8px] border-[1.5px] border-solid border-(--primary-O300) bg-(--primary-O100) px-3 py-2">
          <div className="min-w-0">
            <p className="m-0! truncate [font-size:var(--fs-p4)]! font-semibold text-(--neutral-black-NB1000)!">
              {studentName(value)}
            </p>
            {value.email ? (
              <p className="m-0! truncate [font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
                {value.email}
              </p>
            ) : null}
          </div>
          {!disabled ? (
            <button
              type="button"
              aria-label="Clear selected student"
              onClick={() => onChange(null)}
              className="shrink-0 cursor-pointer rounded p-1 text-(--neutral-black-NB600) hover:text-(--danger-D700)"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Search field — hidden once a student is locked/selected in disabled mode */}
      {!(disabled && value) ? (
        <div className="relative" onMouseDown={() => setOpen(true)}>
          <SearchInput
            value={term}
            onChange={(next) => {
              setTerm(next);
              setOpen(true);
            }}
            placeholder={value ? 'Search to change student' : placeholder}
            fullWidth
            className={disabled ? 'pointer-events-none opacity-50' : undefined}
          />

          {showDropdown ? (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) py-1 shadow-lg">
              {isFetching && students.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-2.5 [font-family:var(--font-montserrat)] [font-size:var(--fs-l4)] text-(--neutral-white-NW600)">
                  <Loader2 className="size-4 animate-spin" />
                  Searching…
                </div>
              ) : students.length === 0 ? (
                <div className="px-4 py-2.5 [font-family:var(--font-montserrat)] [font-size:var(--fs-l4)] text-(--neutral-white-NW600)">
                  No students found
                </div>
              ) : (
                students.map((student) => {
                  const isSelected = value?.id === student.id;
                  return (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => handleSelect(student)}
                      className={cn(
                        'flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left cursor-pointer',
                        '[font-family:var(--font-montserrat)] hover:bg-(--primary-O200)/40',
                        isSelected ? 'bg-(--primary-O200)/40' : ''
                      )}
                    >
                      <span
                        className={cn(
                          '[font-size:var(--fs-l4)] text-(--neutral-black-NB900)',
                          isSelected ? 'font-semibold text-(--primary-O900)' : ''
                        )}
                      >
                        {studentName(student)}
                      </span>
                      {student.email ? (
                        <span className="[font-size:var(--fs-l5)] text-(--neutral-black-NB600)">
                          {student.email}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
